import Container, { Service } from 'typedi';
import { LoggerProvider } from '../../logger';
import { LoaderManager } from '../../../loaders/LoaderManager';
import { IPostLogin } from '../../post-login/models/IPostLogin';
import {
    IAppClosedTopicPayload,
    IAppContainerReadyPayload,
    IDialogShowData,
    ILogger,
    IMessageActionTakenData,
    IMessageDisplayedData,
    IUiChannel,
    MessageBroker,
    PostLoginActionNameEnum,
} from '@sparkware/uc-sdk-core';
import { UIService } from '../../ui';
import { WebLoginService } from '../../web-login/web.login.service';
import { IAppLauncher } from '../../app-launcher/models';
import { IBIEventHandler } from '../../bi/models/IBIEventHandler';
import { IToggleBottomBarData } from '@sparkware/uc-sdk-core/lib/message-broker/channels/ui/ui.channel.interfaces';
import { MBRollout } from '../../native/enums/consts';
import { LocalStoreService } from '../../storage/implementations/store';
import { NativeUtils } from '../../native/native.utils';

@Service()
export class UIChannelSubscriber {
    private readonly _logger: ILogger;
    private readonly _uiChannel: IUiChannel;
    private readonly _uiService: UIService;
    private readonly _webLoginService: WebLoginService;
    private readonly _localStoreService: LocalStoreService;

    private get _postLoginPromise(): Promise<IPostLogin> {
        return LoaderManager.Instance.PostLoginLoader.Instance;
    }

    private get _appLauncherPromise(): Promise<IAppLauncher> {
        return LoaderManager.Instance.AppLauncherLoader.Instance;
    }

    private get _biEventHandlerPromise(): Promise<IBIEventHandler> {
        return LoaderManager.Instance.BIEventHandlerLoader.Instance;
    }

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('UIChannelSubscriber');
        this._uiService = Container.get(UIService);
        this._webLoginService = Container.get(WebLoginService);
        this._localStoreService = Container.get(LocalStoreService);
        this._uiChannel = MessageBroker.getInstance().ui;
        this._uiChannel.topics.favouriteSportsClosed.subscribe(
            this.onFavouriteSportsClosed.bind(this),
        );
        this._uiChannel.topics.Dialog.subscribe(this.onDialog.bind(this));
        this._uiChannel.topics.DeviceInformation.subscribe(this.onDeviceInformation.bind(this));
        this._uiChannel.topics.appContainerReady.subscribe(this.onAppContainerReady.bind(this));
        this._uiChannel.topics.messageDisplayed.subscribe(this.onMessageDisplayed.bind(this));
        this._uiChannel.topics.messageActionTaken.subscribe(this.onMessageActionTaken.bind(this));
        this._uiChannel.topics.toggleBottomBar.subscribe(this.onToggleBottomBar.bind(this));
    }

    private async onFavouriteSportsClosed(): Promise<void> {
        (await this._postLoginPromise)?.completeAction(
            PostLoginActionNameEnum.StartFavouriteSportsFlow,
        );
    }

    private async onDialog(data: IDialogShowData): Promise<void> {
        this._uiService.handleDialog(data);
    }

    private async onDeviceInformation(): Promise<void> {
        this._webLoginService.InitializeWebLogin();
    }

    private async onAppContainerReady(data: IAppContainerReadyPayload): Promise<void> {
        this._logger.debug(`[onAppContainerReady]] start, data: ${JSON.stringify(data)}`);
        const appLauncher = await this._appLauncherPromise;
        await appLauncher?.onAppContainerReady(data);
    }

    private async onMessageDisplayed(data: IMessageDisplayedData): Promise<void> {
        const biEventHandler = await this._biEventHandlerPromise;
        biEventHandler?.onMessageDisplayed(data);
    }

    private async onMessageActionTaken(data: IMessageActionTakenData): Promise<void> {
        const biEventHandler = await this._biEventHandlerPromise;
        biEventHandler?.onMessageActionTaken(data);
    }

    private async onToggleBottomBar(data: IToggleBottomBarData): Promise<void> {
        //This validation is needed becuase if specate native rollout is less than UI
        //We need to call their function, if the native MB rollout is equal to or greater than the UI rollout
        //they will handle the bottom bar visibility themselves
        let nativeMBRollout = Number(this._localStoreService.get('NativeMBRollout'));
        if (nativeMBRollout < MBRollout.UI) {
            await NativeUtils.showBottomBar(data.visible);
        }
    }
}
